/**
 * Os atributos secundários: o que <b>sai de uma conta</b> em vez de ser escolhido.
 *
 * <p>Os seis primários são pontos que o jogador distribui. Estes não se distribuem —
 * eles caem dos primários, do nível e do equipamento por uma fórmula. É por isso que a
 * tela não os deixa marcar: deixa escrever <i>a conta</i>.
 *
 * <p><b>A lista não está fechada.</b> Ao contrário de `ATRIBUTOS`, que é sistema de
 * regras e tem seis, esta cresce conforme o combate for sendo escrito — o Theo já disse
 * que traz mais. Por isso nada aqui (nem título, nem texto de tela) conta quantos são:
 * um número escrito à mão vira mentira no primeiro acréscimo.
 *
 * <p>A fórmula é <b>texto livre</b>, e de propósito. Um construtor de expressão
 * obrigaria a fechar antes a gramática — se existe multiplicação, se o nível entra, se
 * arredonda para baixo — e essas são justamente as perguntas que a tela existe para o
 * time responder. Enquanto forem hipótese, `VIT × 10 + nível × 5` escrito à mão diz mais
 * que qualquer campo estruturado.
 */

export interface Derivado {
  id: string;
  nome: string;
  /** Uma frase do que ele governa — o suficiente para a linha não ser só um nome. */
  sobre: string;
  /**
   * A conta que o time já tem em mente, como <b>sugestão de digitação</b>.
   *
   * <p>Vai no `placeholder`, nunca no valor. Campo pré-preenchido se lê como decisão
   * tomada; em cinza, dentro do campo vazio, ele mostra o formato esperado e continua
   * sendo pergunta. É a mesma escolha das duas tier lists.
   */
  exemplo: string;
  /**
   * A perícia que <b>entra na conta</b> deste secundário — o id dela.
   *
   * <p>Esquiva e Bloqueio aparecem duas vezes no sistema, e por um bom motivo: a perícia
   * é o que o personagem treina, e o secundário é o número que a mesa consulta. O
   * segundo se calcula a partir do primeiro — a fórmula da Esquiva soma o nível da
   * perícia Esquiva.
   *
   * <p>Declarar isso aqui é o que faz a tela dizer <i>"usa a perícia Esquiva"</i> em vez
   * de só alertar que o nome se repete. A diferença não é de redação: um aviso de nome
   * repetido convida a "corrigir" um dos dois, e apagar qualquer um deles quebraria a
   * conta do outro.
   *
   * <p>Fica vazio quando não há uma perícia só: o Acerto usa <b>a perícia da arma</b>,
   * que muda a cada arma empunhada, e apontar para uma delas seria mentira.
   */
  usaPericia?: string;
  /**
   * Cor própria, quando o item pede uma que o grupo não dá.
   *
   * <p>É o caso do HP e do MP: as duas barras precisam ser <b>reconhecíveis de longe e
   * distintas entre si</b>, porque é assim que elas vão aparecer no jogo. Herdar a cor do
   * grupo faria as duas iguais, que é o oposto do que a interface do combate vai fazer.
   */
  cor?: string;
  /**
   * As medidas do mesmo secundário, quando ele tem mais de uma.
   *
   * <p>Acerto e Dano são medidos <b>um por tipo de ataque</b>. Cada variante tem fórmula
   * própria — cortante e à distância não saem da mesma conta —, mas todas moram na mesma
   * linha, porque a pergunta é uma só. Seis linhas soltas diriam que são seis grandezas
   * diferentes, e enterrariam Iniciativa e Defesa no meio delas.
   *
   * <p>Sem variantes, o secundário tem uma fórmula e pronto — que é o caso dos treze.
   */
  variantes?: Variante[];
}

export interface Variante {
  id: string;
  rotulo: string;
}

/**
 * Os três tipos de ataque do alpha.
 *
 * <p>A ficha de lá mostra <b>três números</b> em Acerto e em Dano, um por tipo. Eles não
 * são três secundários diferentes: são a mesma grandeza medida três vezes, e é por isso
 * que ficam numa linha só em vez de virarem seis. Quem lê "Acerto" quer ver os três
 * lado a lado, como no jogo.
 */
export const TIPOS_DE_ATAQUE: Variante[] = [
  { id: 'cortante', rotulo: 'Cortante' },
  { id: 'perfurante', rotulo: 'Perfurante' },
  { id: 'aDistancia', rotulo: 'À distância' },
];

export interface GrupoDeDerivados {
  id: string;
  titulo: string;
  sobre: string;
  /** A cor do grupo — é ela que o item herda quando não tem uma razão para ter a sua. */
  cor: string;
  itens: Derivado[];
}

/**
 * A cor de um secundário, na ordem em que se decide.
 *
 * <p>Quem <b>usa uma perícia herda a cor dela</b>, e isso não é economia de paleta: a
 * Esquiva secundária sai azul-defensiva porque a perícia Esquiva é azul-defensiva, e ver
 * a mesma cor nas duas seções é o que faz a ligação ser lida sem precisar do selo. Um
 * item com cor própria vem antes de tudo; o resto é a cor do grupo.
 */
export function corDoDerivado(d: Derivado, grupo: GrupoDeDerivados, corDaPericia: (id: string) => string | undefined) {
  return d.cor ?? (d.usaPericia ? corDaPericia(d.usaPericia) : undefined) ?? grupo.cor;
}

export const DERIVADOS: GrupoDeDerivados[] = [
  {
    id: 'progressao',
    titulo: 'Progressão',
    cor: '#c9a25a',
    sobre:
      'O nível não é um atributo — é o relógio de todos eles. Estas três contas decidem o ritmo do jogo inteiro: quanto se joga para subir, quanto se ganha ao subir, e quanto custa gastar o que se ganhou.',
    itens: [
      {
        id: 'xp-do-nivel',
        nome: 'XP para passar de nível',
        sobre: 'Quanto a barra exige em cada degrau.',
        exemplo: 'ex.: nível² × 100',
      },
      {
        id: 'pontos-por-nivel',
        nome: 'Pontos de atributo por nível',
        sobre: 'Quantos pontos o nível novo entrega para distribuir.',
        exemplo: 'ex.: 3 por nível',
      },
      {
        id: 'custo-do-ponto',
        nome: 'Custo de um ponto de atributo',
        sobre: 'Quantos pontos custa subir o atributo mais um.',
        exemplo: 'ex.: 1, ou o valor atual ÷ 5',
      },
    ],
  },
  {
    id: 'vitais',
    titulo: 'Vitais',
    cor: '#b06a6a',
    sobre:
      'As duas barras. São o que separa uma luta difícil de uma luta perdida, e o que dá peso a Vitalidade e Sabedoria fora da rolagem.',
    itens: [
      {
        id: 'hp',
        nome: 'HP',
        sobre: 'Quanto dano o corpo aguenta antes de cair.',
        exemplo: 'ex.: VIT × 10 + nível × 5',
        cor: '#c1585e',
      },
      /*
       * O alpha chama de **SP**, e o id acompanhou. Diferente das perícias — onde o id é
       * chave de ícone e de rolagem compartilhada, e por isso `maca` continua `maca` —,
       * aqui ele só endereça um rascunho de fórmula. Trocar sai barato, e `lerFormulas`
       * carrega o que tiver sido escrito em `mp` para `sp` uma vez.
       */
      {
        id: 'sp',
        nome: 'SP',
        sobre: 'O combustível do que não é físico.',
        exemplo: 'ex.: INT × 8',
        cor: '#5f8fc4',
      },
      {
        id: 'turnos',
        nome: 'Turnos',
        sobre: 'Quantas ações o dia comporta antes de exigir descanso.',
        exemplo: 'ex.: 8 + VIT ÷ 4',
        cor: '#a08a4a',
      },
    ],
  },
  {
    id: 'combate',
    titulo: 'Combate',
    cor: '#98a7b8',
    sobre:
      'Os números que a troca de golpes consulta. Estão aqui, e não na tela de Combate, porque são do personagem: a tela de Combate diz quando cada um é consultado, esta diz de onde ele vem. Alguns somam uma perícia — a Esquiva do personagem entra na conta da Esquiva dele —, e a linha diz qual.',
    itens: [
      {
        id: 'iniciativa',
        nome: 'Iniciativa',
        sobre: 'O que decide quem age primeiro.',
        exemplo: 'ex.: AGI + PER',
      },
      /* Acerto e Dano medem uma vez por tipo de ataque — ver `TIPOS_DE_ATAQUE`. */
      {
        id: 'acerto',
        nome: 'Acerto',
        sobre: 'O que o golpe precisa vencer para chegar.',
        exemplo: 'ex.: DES + perícia da arma',
        variantes: TIPOS_DE_ATAQUE,
      },
      {
        id: 'dano',
        nome: 'Dano',
        sobre: 'Quanto o golpe tira quando chega.',
        exemplo: 'ex.: arma + FOR ÷ 2',
        cor: '#c1584e',
        variantes: TIPOS_DE_ATAQUE,
      },
      {
        id: 'esquiva',
        nome: 'Esquiva',
        sobre: 'Não estar onde o golpe chega.',
        exemplo: 'ex.: AGI × 2 + Esquiva',
        usaPericia: 'esquiva',
      },
      {
        id: 'defesa',
        nome: 'Defesa',
        sobre: 'O que a armadura absorve do que passou.',
        exemplo: 'ex.: armadura + VIT ÷ 2',
      },
      {
        id: 'bloqueio',
        nome: 'Bloqueio',
        sobre: 'Estar, e aguentar — o que o escudo tira.',
        exemplo: 'ex.: FOR + Bloqueio',
        usaPericia: 'bloqueio',
      },
    ],
  },
  /*
   * O quarto grupo do alpha, e o que menos se parece com os outros três: aqui nada é um
   * número que se compara. São **estados** — quanto se está carregando, como se dormiu, o
   * que está pegando. Ficam juntos porque é assim que a ficha do jogo os agrupa, e
   * espalhá-los entre os outros faria a comparação com o alpha exigir tradução.
   *
   * <p>Cada um pede mais que uma conta: o peso tem uma faixa qualitativa ("Super Leve"),
   * o sono devolve turnos, e a condição é um efeito com nome próprio. As fórmulas aqui
   * vão ser mais texto que aritmética — e é justamente por isso que o campo é livre.
   */
  {
    id: 'informacoes',
    titulo: 'Informações',
    sobre:
      'O que a ficha do alpha mostra abaixo dos números: estado, e não atributo. Nenhum destes se soma numa rolagem — eles mudam o que o personagem pode fazer no dia.',
    cor: '#a89a7c',
    itens: [
      {
        id: 'peso-carregado',
        nome: 'Peso Carregado',
        sobre: 'Quanto cabe na mochila, e a faixa em que o peso atual cai.',
        exemplo: 'ex.: FOR × 2 + 11, com faixas de Super Leve a Sobrecarregado',
      },
      {
        id: 'qualidade-do-sono',
        nome: 'Qualidade do Sono',
        sobre: 'Quantos turnos a noite devolve, e o que muda conforme onde e como se dormiu.',
        exemplo: 'ex.: turnos base + bônus da cama e da refeição',
      },
      {
        id: 'condicao',
        nome: 'Condição',
        sobre: 'O efeito ativo no personagem — o que ele concede enquanto durar.',
        exemplo: 'ex.: o que a condição soma, e por quantos turnos',
      },
    ],
  },
];

export const TODOS_OS_DERIVADOS: Derivado[] = DERIVADOS.flatMap((g) => g.itens);

export const CHAVE_DERIVADOS = 'gg-pilot:ficha:derivados';

/** A fórmula de cada secundário, por id. Texto livre, vazio enquanto for pergunta. */
export type Formulas = Record<string, string>;

/**
 * A chave da fórmula de um secundário, ou de uma variante dele.
 *
 * <p>`acerto:cortante` em vez de `acerto`. O separador importa: sem ele, um secundário
 * novo chamado `acertocortante` colidiria com a variante — improvável, mas o custo de
 * evitar é um caractere.
 */
export const chaveDaFormula = (d: Derivado, variante?: Variante) =>
  variante ? `${d.id}:${variante.id}` : d.id;

/** Todas as chaves de fórmula: uma por secundário sem variante, três nos que têm. */
export const CHAVES_DE_FORMULA: string[] = TODOS_OS_DERIVADOS.flatMap((d) =>
  d.variantes ? d.variantes.map((v) => chaveDaFormula(d, v)) : [chaveDaFormula(d)]
);

export const formulasEmBranco = (): Formulas =>
  Object.fromEntries(CHAVES_DE_FORMULA.map((c) => [c, '']));

/**
 * As fórmulas a partir do que estava gravado, reconstruindo sobre o branco.
 *
 * <p>Reconstruir é o que faz um secundário novo — e vem mais — não quebrar o que já foi
 * escrito, e um que saia da lista não ficar assombrando o rascunho.
 *
 * <p>É por aqui que a Personagem lê hoje, com o documento vindo do GG Pilot. As duas
 * renomeações tratadas abaixo continuam valendo: elas descrevem o formato do dado, e o
 * dado migrado do navegador carrega as mesmas cicatrizes.
 */
export function interpretarFormulas(guardado: unknown): Formulas {
  const base = formulasEmBranco();
  try {
    if (!guardado || typeof guardado !== 'object') return base;
    const lido = guardado as Record<string, unknown>;
    for (const id of Object.keys(base)) {
      if (typeof lido[id] === 'string') base[id] = lido[id] as string;
    }
    /*
     * `mp` virou `sp` quando a ficha do alpha mostrou o nome que o jogo usa. Carrega o
     * que estava escrito, uma vez — jogar fora uma fórmula do time por causa de uma
     * renomeação seria perda gratuita.
     */
    if (!base.sp && typeof lido.mp === 'string') base.sp = lido.mp;

    /*
     * Acerto e Dano tinham UMA fórmula antes de virarem três. O que estiver escrito na
     * chave antiga vai para a **primeira** variante, e não para as três: uma conta
     * copiada em triplicado pareceria decidida, quando ela foi escrita antes de o time
     * saber que havia três tipos. Na primeira, ela continua visível e o resto fica em
     * branco — que é a pergunta em aberto de verdade.
     */
    for (const d of TODOS_OS_DERIVADOS) {
      const primeira = d.variantes?.[0];
      if (!primeira) continue;
      const chave = chaveDaFormula(d, primeira);
      if (!base[chave] && typeof lido[d.id] === 'string') base[chave] = lido[d.id] as string;
    }
    return base;
  } catch {
    return base;
  }
}

/**
 * O rascunho que ficou no `localStorage`, para a migração de uma vez só.
 *
 * <p>Não é mais a fonte: as fórmulas moram no GG Pilot. Sobrevive como ponte, para quem
 * já tinha escrito no navegador encontrar tudo gravado na primeira abertura.
 */
export function lerFormulas(): Formulas {
  try {
    const cru = localStorage.getItem(CHAVE_DERIVADOS);
    return interpretarFormulas(cru ? JSON.parse(cru) : null);
  } catch {
    return formulasEmBranco();
  }
}

/** O rascunho do navegador, ou nada — ver `rolagensDoNavegador` sobre a diferença. */
export function formulasDoNavegador(): Formulas | undefined {
  try {
    return localStorage.getItem(CHAVE_DERIVADOS) == null ? undefined : lerFormulas();
  } catch {
    return undefined;
  }
}

/** Quantas fórmulas já foram escritas. Espaço em branco não conta como resposta. */
/**
 * Quantas fórmulas já foram escritas, contando cada variante.
 *
 * <p>Conta por <b>chave</b>, e não por secundário: com Acerto e Dano em três medidas
 * cada, um denominador por linha diria "13" quando há 17 contas a escrever, e o
 * contador nunca fecharia certo.
 */
export const escritas = (formulas: Formulas) =>
  CHAVES_DE_FORMULA.filter((c) => (formulas[c] ?? '').trim()).length;
